import { AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { REQUEST_TIMEOUT } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { handleAuthFailure } from '@/utils/authFailure'
import { isIdleExemptRoute, isIdleExpired } from '@/utils/idleTimeout'
import { ensureValidAccessToken, isAccessTokenExpired, refreshAccessToken } from '@/utils/tokenRefresh'
import axios from 'axios'

const userStore = useUserStoreWithOut()

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH
const HAS_API_PREFIX_IN_BASE_URL =
  typeof PATH_URL === 'string' && /\/api\/?$/.test(PATH_URL.trim())

const abortControllerMap: Map<string, AbortController> = new Map()

/** 403 权限提示节流，避免同一页面多接口并发时刷屏 */
let lastPermissionDeniedTipAt = 0
const tipPermissionDenied = () => {
  const now = Date.now()
  if (now - lastPermissionDeniedTipAt < 5000) return
  lastPermissionDeniedTipAt = now
  ElMessage.warning('当前账号无该接口权限')
}

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  // Normalize duplicate API prefix:
  // baseURL is '/api', while some APIs still declare url as '/api/**'.
  // Without normalization this becomes '/api/api/**' and backend returns errors.
  if (
    HAS_API_PREFIX_IN_BASE_URL &&
    typeof res.url === 'string' &&
    res.url.startsWith('/api/')
  ) {
    res.url = res.url.replace(/^\/api/, '')
  }
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(
    import.meta.env.VITE_USE_MOCK === 'true' ? url.replace('/mock', '') : url,
    controller
  )
  return res
})

/** 发请求前若 accessToken 已过期，先用 refreshToken 静默续期；空闲超时则直接退出 */
axiosInstance.interceptors.request.use(async (res: InternalAxiosRequestConfig) => {
  const store = useUserStoreWithOut()
  const token = store.getToken
  if (token) {
    // 空闲超时优先于续期：禁止无操作超时时静默刷新
    if (!isIdleExemptRoute() && isIdleExpired()) {
      handleAuthFailure({ reason: 'idle' })
      return Promise.reject(new axios.CanceledError('登录已过期'))
    }
    const validToken = await ensureValidAccessToken()
    if (validToken) {
      const tokenKey = store.getTokenKey ?? 'Authorization'
      res.headers = res.headers || {}
      res.headers[tokenKey] = `Bearer ${validToken}`
    } else if (isAccessTokenExpired(token) || isIdleExpired()) {
      if (!isIdleExemptRoute() && isIdleExpired()) {
        handleAuthFailure({ reason: 'idle' })
      } else {
        setTimeout(() => handleAuthFailure(), 1500)
      }
      return Promise.reject(new axios.CanceledError('登录已过期'))
    }
  }
  return res
})

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res
  },
  async (error: AxiosError) => {
    console.log('err： ' + error) // for debug

    const status = error.response?.status
    const config = error.config as (InternalAxiosRequestConfig & { _authRetried?: boolean }) | undefined
    const hadToken = !!localStorage.getItem('accessToken')

    // 403 = 菜单/接口权限不足，≠ 登录失效；禁止 refresh、禁止清 token、禁止跳登录
    if (status === 403) {
      tipPermissionDenied()
      return Promise.reject(error)
    }

    // 401：未认证 / token 失效，可 refresh 后重试一次；失败再踢登录（与空闲超时逻辑独立）
    if (status === 401 && config && hadToken && !config._authRetried) {
      config._authRetried = true
      if (!isIdleExemptRoute() && isIdleExpired()) {
        handleAuthFailure({ reason: 'idle' })
        return Promise.reject(error)
      }
      const newToken = await refreshAccessToken()
      if (newToken) {
        const userStore = useUserStoreWithOut()
        const tokenKey = userStore.getTokenKey ?? 'Authorization'
        config.headers = config.headers || {}
        config.headers[tokenKey] = `Bearer ${newToken}`
        return axiosInstance.request(config)
      }
      setTimeout(() => handleAuthFailure(), 1500)
    } else if (status === 401 && hadToken) {
      if (!isIdleExemptRoute() && isIdleExpired()) {
        handleAuthFailure({ reason: 'idle' })
      } else {
        setTimeout(() => handleAuthFailure(), 1500)
      }
    }

    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
